import React from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Row,
  Col,
} from 'antd';
import type { FormInstance } from 'antd';
import { NumericInput, RichTextEditor } from '../common';
import { ChecklistItemForm } from './ChecklistItemForm';
import type { ChecklistItem } from '../../../../shared/types/work-order-template.types';

const PRIORITY_OPTIONS = [
  { label: '低', value: 'low' },
  { label: '中', value: 'medium' },
  { label: '高', value: 'high' },
  { label: '緊急', value: 'urgent' }
];

interface WorkTypeCategory {
  id: string;
  categoryLabel?: string | null;
  color?: string | null;
  icon?: string | null;
}

interface UserOption {
  label: string;
  value: string;
}

export interface WorkOrderTemplateFormProps {
  form: FormInstance;
  checklistItems: ChecklistItem[];
  onChecklistItemsChange: (items: ChecklistItem[]) => void;
  workTypeCategories: WorkTypeCategory[];
  isLoadingWorkTypeCategories?: boolean;
  /** 承認設定を表示するかどうか（編集時のみ） */
  showApprovalSettings?: boolean;
  /** 承認者選択用のユーザーリスト */
  userOptions?: UserOption[];
  /** フォームの初期値 */
  initialValues?: {
    orderIndex?: number;
    priority?: string;
  };
}

/**
 * 作業指示書テンプレートの共通フォームコンポーネント
 * 新規作成と編集の両方で使用
 */
export const WorkOrderTemplateForm: React.FC<WorkOrderTemplateFormProps> = ({
  form,
  checklistItems,
  onChecklistItemsChange,
  workTypeCategories,
  isLoadingWorkTypeCategories = false,
  showApprovalSettings = false,
  userOptions = [],
}) => {
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        orderIndex: 0,
        priority: 'medium'
      }}
    >
      {/* 行1: 名前と作業カテゴリ */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="名前"
            name="templateName"
            rules={[{ required: true, message: '名前を入力してください' }]}
          >
            <Input placeholder="名前" maxLength={255} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="作業カテゴリ"
            name="workTypeCategoryId"
            rules={[{ required: true, message: '作業カテゴリを選択してください' }]}
          >
            <Select
              placeholder="作業カテゴリを選択"
              loading={isLoadingWorkTypeCategories}
              allowClear
            >
              {workTypeCategories.map((cat) => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.categoryLabel}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* 行2: 優先度と標準作業時間 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="優先度"
            name="priority"
            rules={[{ required: true, message: '優先度を選択してください' }]}
          >
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="標準作業時間 (時間)" name="standardHours">
            <NumericInput min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      {/* 詳細 */}
      <Form.Item
        label="詳細"
        name="description"
        getValueFromEvent={(markdown: string) => markdown}
        getValueProps={(value) => ({ value: value || '' })}
      >
        <RichTextEditor
          placeholder="テンプレートの詳細..."
          minHeight={100}
          showPreviewToggle={false}
        />
      </Form.Item>

      {/* 指示文 */}
      <Form.Item
        label="指示文"
        name="instructions"
        getValueFromEvent={(markdown: string) => markdown}
        getValueProps={(value) => ({ value: value || '' })}
      >
        <RichTextEditor
          placeholder="作業手順や注意事項など..."
          minHeight={100}
          showPreviewToggle={false}
        />
      </Form.Item>

      {/* 安全注意事項 */}
      <Form.Item
        label="安全注意事項"
        name="safetyNotes"
        getValueFromEvent={(markdown: string) => markdown}
        getValueProps={(value) => ({ value: value || '' })}
      >
        <RichTextEditor
          placeholder="安全上の注意点..."
          minHeight={80}
          showPreviewToggle={false}
        />
      </Form.Item>

      {/* 必要ツール・パーツ */}
      <Form.Item label="必要ツール (カンマ区切り)" name="requiredTools">
        <Input placeholder="例: ドライバー, 点検キット" />
      </Form.Item>

      <Form.Item label="必要パーツ (カンマ区切り)" name="requiredParts">
        <Input placeholder="例: 交換用フィルター, ボルト" />
      </Form.Item>

      {/* 承認設定（編集時のみ表示） */}
      {showApprovalSettings && (
        <>
          <Form.Item
            label="承認が必要"
            name="requiresApproval"
            valuePropName="checked"
            tooltip="この作業指示書を使用したスケジュールの完了報告に承認が必要な場合はONにしてください"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.requiresApproval !== currentValues.requiresApproval
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('requiresApproval') ? (
                <>
                  <Form.Item
                    label="承認者"
                    name="approverUserIds"
                    tooltip="承認可能なユーザーを選択してください（複数選択可）"
                  >
                    <Select
                      mode="multiple"
                      placeholder="承認者を選択"
                      options={userOptions}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="承認可能なロール"
                    name="approverRoles"
                    tooltip="承認可能なロールを選択してください（複数選択可）"
                  >
                    <Select
                      mode="multiple"
                      placeholder="承認可能なロールを選択"
                      options={[
                        { label: '管理者', value: 'admin' },
                        { label: 'マネージャー', value: 'manager' },
                        { label: 'オペレーター', value: 'operator' }
                      ]}
                    />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>
        </>
      )}

      {/* チェックリスト項目 */}
      <Form.Item label="チェックリスト項目">
        <ChecklistItemForm
          items={checklistItems}
          onChange={onChecklistItemsChange}
          disabled={false}
        />
      </Form.Item>
    </Form>
  );
};

export default WorkOrderTemplateForm;
